<template>
  <div class="bg-white rounded-lg shadow-sm overflow-hidden">
    <div class="flex">
      <!-- Left Side: Company Info -->
      <div class="w-1/4 p-4 border-r border-gray-200 relative">
        <div class="flex items-center gap-2">
          <div class="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
            <img 
              v-if="company.logo_url" 
              :src="company.logo_url" 
              :alt="company.name"
              class="w-full h-full object-contain rounded-lg"
            >
            <Building v-else class="w-6 h-6 text-gray-500" />
          </div>
          <div class="flex-1">
            <h3 class="text-lg font-medium flex items-center space-x-2">
              <span>{{ company.name }}</span>
            </h3>
            <p class="text-sm text-gray-500">
              {{ filteredReferrers.length }} referrer{{ filteredReferrers.length !== 1 ? 's' : '' }}
            </p>
          </div>
        </div>
        <div class="absolute bottom-2 right-2">
          <Button variant="outline" size="sm" @click="showCompanyDetails">
            <MoreHorizontal class="w-4 h-4" />
          </Button>
        </div>
      </div>

      <!-- Right Side: Company Notes and Referrers List -->
      <div class="w-3/4 p-4">
        <!-- Company Notes Section (if notes exist) -->
        <div v-if="company.outtalent_internal_notes" class="mb-4 p-4 bg-cosmicPurple/10 rounded-lg">
          <div class="text-sm text-gray-500 flex items-center mb-2">
            <NotebookPen class="w-4 h-4 mr-1" />
            Staff notes:
          </div>
          <p class="text-sm text-gray-600 whitespace-pre-wrap">
            {{ company.outtalent_internal_notes }}
          </p>
        </div>

        <div v-if="filteredReferrers.length > 0" class="space-y-3">
          <ReferrerDataContainer
            v-for="referrer in filteredReferrers" 
            :key="referrer.id"
            :referrer="referrer"
            :company-id="company.id"
            :is-default="company.default_referrer === referrer.id"
            :is-expanded="expandedReferrers.has(referrer.id)"
            @set-as-default="setAsDefault"
            @toggle-expansion="toggleReferrerExpansion"
            @update-referrer="handleReferrerUpdate"
            @remove-referrer="handleReferrerRemoval"
          />
        </div>
        <!-- Empty State -->
        <div 
          v-else 
          class="text-center text-gray-500 py-8"
        >
          No referrers for this company
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { Building, NotebookPen, MoreHorizontal } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import ReferrerDataContainer from '@/components/referrers/ReferrerDataContainer.vue'
import { useReferrersStore } from '@/stores/referrers'

const props = defineProps({
  company: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['showCompanyDetails'])

const referrersStore = useReferrersStore()

// Track which referrers are expanded
const expandedReferrers = ref(new Set())

// Track which referrers have been hidden
const hiddenReferrers = ref(new Set())

// Compute filtered referrers (excluding hidden ones)
const filteredReferrers = computed(() => {
  return props.company.referring_people.filter(referrer => 
    !hiddenReferrers.value.has(referrer.id)
  )
})

// Handle setting a referrer as default for a company
const setAsDefault = async (companyId, referrerId) => {
  try {
    await referrersStore.setDefaultReferrer(companyId, referrerId)
  } catch (error) {
    console.error('Failed to set default referrer:', error)
  }
}

// Toggle referrer expansion
const toggleReferrerExpansion = (referrerId) => {
  if (expandedReferrers.value.has(referrerId)) {
    expandedReferrers.value.delete(referrerId)
  } else {
    expandedReferrers.value.add(referrerId)
  }
}

// Handle referrer updates from child component
const handleReferrerUpdate = () => {
  // This is handled by the ReferrerCard component directly
  // We can add any additional logic here if needed
}

// Handle referrer removal (hiding) from UI
const handleReferrerRemoval = (referrerId) => {
  hiddenReferrers.value.add(referrerId)
  // Also remove from expanded set if it was expanded
  expandedReferrers.value.delete(referrerId)
}

// Show company details modal
const showCompanyDetails = () => {
  emit('showCompanyDetails', props.company)
}

</script> 